import React, { useContext } from 'react';

import { type I18NMessages, IntlMessagesProvider } from '@atlaskit/intl-messages-provider';
import { fg } from '@atlaskit/platform-feature-flags';

import messages from '../../../i18n/en';

import { asyncLanguages } from './async-languages';

// Sentinel context: true means an IntlMessagesProvider is already mounted in a parent
const AsyncIntlProviderMountedContext = React.createContext(false);

export function AsyncIntlProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
	const alreadyMounted = useContext(AsyncIntlProviderMountedContext);

	// If a parent NavErrorBoundary already mounted the provider, skip the redundant fetch.
	// The FG must come first because it is introducing the change.
	// eslint-disable-next-line @atlaskit/platform/no-preconditioning
	if (fg('navx-4615-fix-async-intl-for-gsn') && alreadyMounted) {
		return <>{children}</>;
	}

	return (
		<AsyncIntlProviderMountedContext.Provider value={true}>
			<IntlMessagesProvider defaultMessages={messages} loaderFn={fetchMessagesForLocale}>
				{children}
			</IntlMessagesProvider>
		</AsyncIntlProviderMountedContext.Provider>
	);
}

async function fetchMessagesForLocale(locale: string): Promise<I18NMessages | undefined> {
	try {
		// Fallback to the generic locale if specified one is not found
		const languageLocale = locale in asyncLanguages ? locale : locale.split(/[-_]/)[0] || '';

		// Dynamic import of the locale file
		const localeMessages = (await asyncLanguages[languageLocale]?.()) as
			| { default: I18NMessages }
			| undefined;

		return localeMessages?.default ?? messages;
	} catch {
		// ignore
	}

	return messages;
}
